import { ElementType } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Picture, PictureProps } from "@/components/wpl/image/src/image";
import {
    Button,
    ButtonProps
} from "@/libraries/design/components/button/src/button";
import { Link, LinkProps } from "@/libraries/design/components/link/src/link";
import { wacToken } from "@/libraries/design/foundations";
import { IconSvgProps } from "@/libraries/wpl/foundations/src/icons/types";

export type VideoProps = {
    readonly src: string;
    readonly poster?: string;
    readonly autoplay?: boolean;
    readonly muted?: boolean;
    readonly loop?: boolean;
    readonly controls?: boolean;
    readonly width?: string | number;
    readonly height?: string | number;
    readonly preload?: "none" | "metadata" | "auto";
    readonly alt?: string;
    readonly captions?: {
        readonly src: string;
        readonly srclang?: string;
        readonly label?: string;
    };
};

export interface ButtonAndLinkProps {
    readonly button: ButtonProps;
    readonly buttonText: string;
    readonly link?: LinkProps;
}

export interface OneLinkProps {
    readonly link: LinkProps;
}

export interface OneButtonProps {
    readonly button: ButtonProps;
    readonly buttonText: string;
}

export type FeaturedInfoCardCtaProps =
    | ButtonAndLinkProps
    | OneLinkProps
    | OneButtonProps;

export type FeaturedInfoCardProps = {
    readonly Icon?: ElementType<IconSvgProps>;
    readonly iconSize?: string;
    readonly title: string;
    readonly body?: string;
    readonly image?: PictureProps;
    readonly video?: VideoProps;
    readonly ctaProps?: FeaturedInfoCardCtaProps;
};

const containerStyles = css({
    "containerType": "inline-size",
    "width": "100%",
    "height": "100%",
    "@container (width > 48rem)": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> div": {
            "flexDirection": "row",
            "alignItems": "stretch",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".image, .video": {
                flex: 7,
                width: "auto"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ".body": {
                flex: 5,
                justifyContent: "center"
            }
        }
    }
});
const cardStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "alignItems": "flex-start",
    "flexShrink": "0",
    "borderRadius": `${token("space.100")}`,
    "width": "100%",
    "height": "100%",
    "overflow": "clip",
    "background": wacToken("color.neutral.light0"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".image, .video": {
        lineHeight: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".video video": {
        width: "100%",
        height: "auto",
        display: "block"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p, h3": {
        margin: 0,
        lineHeight: 1.25
    }
});
const cardBodyStyles = css({
    "display": "flex",
    "flexDirection": "column",
    "padding": `${token("space.400")}`,
    "alignItems": "flex-start",
    "gap": "10px",
    "flexShrink": "0",
    "borderRadius": `${token("space.100")}`,
    "background": wacToken("color.neutral.light0"),
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p, h3": {
        margin: 0,
        lineHeight: 1.25
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        lineHeight: "1.5rem"
    }
});

const ctaStyles = css({
    display: "flex",
    flexWrap: "wrap",
    gap: token("space.250"),
    alignItems: "center",
    justifyContent: "flex-start"
});

interface RenderCtaProps {
    ctaProps: FeaturedInfoCardCtaProps;
}

const renderCta = ({ ctaProps }: RenderCtaProps) => {
    if ("button" in ctaProps && ctaProps.button !== undefined) {
        // Button + optional Link case
        return (
            <div css={ctaStyles}>
                <Button {...ctaProps.button}>{ctaProps.buttonText}</Button>
                {"link" in ctaProps && ctaProps.link && (
                    // eslint-disable-next-line  @atlassian/a11y/anchor-has-content
                    <Link {...ctaProps.link} />
                )}
            </div>
        );
    }

    // Link only case
    if ("link" in ctaProps && ctaProps.link !== undefined) {
        return (
            <div css={ctaStyles}>
                {/* eslint-disable-next-line  @atlassian/a11y/anchor-has-content */}
                <Link {...ctaProps.link} />
            </div>
        );
    }

    return null;
};

export const FeaturedInfoCard = ({
    Icon,
    title,
    body,
    iconSize = "24",
    image,
    video,
    ctaProps
}: // eslint-disable-next-line complexity
FeaturedInfoCardProps) => {
    return (
        <div className="featured-info-card" css={containerStyles}>
            <div css={cardStyles}>
                {video ? (
                    <div className="video">
                        <video
                            src={video.src}
                            poster={video.poster}
                            autoPlay={video.autoplay}
                            muted={video.muted}
                            loop={video.loop}
                            controls={video.controls ?? true}
                            width={video.width}
                            height={video.height}
                            preload={video.preload ?? "metadata"}
                            aria-label={video.alt || title}
                            style={
                                video.width && video.height
                                    ? {
                                          aspectRatio: `${video.width}/${video.height}`
                                      }
                                    : undefined
                            }
                        >
                            <track
                                kind="captions"
                                src={video.captions?.src || ""}
                                srcLang={video.captions?.srclang || "en"}
                                label={video.captions?.label || "Captions"}
                                default
                            />
                        </video>
                    </div>
                ) : image ? (
                    <div className="image">
                        <Picture {...image} />
                    </div>
                ) : null}
                <div css={cardBodyStyles} className="body">
                    {Icon && (
                        <div>
                            <Icon size={iconSize} />
                        </div>
                    )}
                    <h3 className="h4">{title}</h3>
                    {body && <p>{body}</p>}
                    {ctaProps && renderCta({ ctaProps })}
                </div>
            </div>
        </div>
    );
};
